import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Plus, CalendarDays, ClipboardList, LogOut, ArrowRight, Clock, MapPin, ChevronDown, Shield, Wrench, ClipboardCheck, Repeat
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  pending: { label: 'In attesa', cls: 'status-pending' },
  accepted: { label: 'Accettato', cls: 'status-scheduled' },
  chat_complete: { label: 'Chat completata', cls: 'status-scheduled' },
  booked: { label: 'Prenotato', cls: 'status-in_progress' },
  paid: { label: 'Pagato', cls: 'status-paid' },
  completed: { label: 'Completato', cls: 'status-completed' },
  scheduled: { label: 'Programmato', cls: 'status-scheduled' },
  in_progress: { label: 'In corso', cls: 'status-in_progress' },
  cancelled: { label: 'Annullato', cls: 'status-cancelled' },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auto-redirect if coming from QR code
    const savedServizio = localStorage.getItem('regola_servizio');
    if (savedServizio) {
      localStorage.removeItem('regola_servizio');
      if (savedServizio === 'check_generale') {
        navigate('/check-generale', { replace: true });
        return;
      } else if (savedServizio === 'una_tantum') {
        navigate('/preventivo', { replace: true });
        return;
      } else if (savedServizio === 'abbonamento') {
        navigate('/abbonamento', { replace: true });
        return;
      }
    }
    Promise.all([api.get('/quotes'), api.get('/appointments')])
      .then(([qRes, aRes]) => {
        setQuotes(qRes.data);
        setAppointments(aRes.data);
      })
      .catch(() => toast.error('Errore nel caricamento dati'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const getNextAction = (q) => {
    if (q.status === 'pending') return { label: 'Accetta', path: null, action: 'accept' };
    if (q.status === 'accepted') return { label: 'Chat AI', path: `/chat/${q.quote_id}` };
    if (q.status === 'chat_complete') return { label: 'Prenota', path: `/calendario/${q.quote_id}` };
    if (q.status === 'booked') return { label: 'Pagamento', path: `/pagamento/${q.quote_id}` };
    return null;
  };

  const handleAccept = async (quoteId) => {
    try {
      await api.put(`/quotes/${quoteId}/accept`);
      setQuotes((prev) => prev.map((q) => q.quote_id === quoteId ? { ...q, status: 'accepted' } : q));
      toast.success('Preventivo accettato!');
    } catch {
      toast.error('Errore nell\'accettazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const upcoming = appointments.filter((a) => a.status === 'scheduled' || a.status === 'in_progress');

  return (
    <div className="min-h-screen bg-stone-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <img src="https://static.prod-images.emergentagent.com/jobs/b0feedf7-36df-41e9-9474-99c5d4f77205/images/70da477a4272b0edc6ede2f5e422bf85f189422131684b511c96d6b04c9062b9.jpeg" alt="REGOLA" className="h-10" />
          <div className="flex items-center gap-3">
            {(user?.role === 'admin' || user?.email === 'info@assistenzaregola.it' || user?.email === 'sartoririccardo1987@gmail.com') && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs font-semibold border-blue-200 text-blue-600 hover:bg-blue-50 gap-1.5"
                onClick={() => navigate('/admin')}
                data-testid="admin-btn"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {(user?.role === 'admin' || user?.email === 'info@assistenzaregola.it') && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                    <Shield className="w-4 h-4 mr-2" /> Pannello Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h2 className="text-base md:text-lg font-bold text-slate-900">
            Ciao, {user?.name?.split(' ')[0]}!
          </h2>
          <p className="text-sm text-slate-500 mt-1">Gestisci i tuoi preventivi e appuntamenti</p>
        </div>

        {/* Service Options */}
        <div className="mb-10">
          <h3 className="font-bold text-slate-900 mb-4">Cosa desideri fare?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card
              className="card-hover cursor-pointer border-0 shadow-sm rounded-xl overflow-hidden"
              onClick={() => navigate('/preventivo')}
              data-testid="service-una-tantum"
            >
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center mb-3">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-sm text-slate-900 mb-1">Intervento a Chiamata</p>
                <p className="text-xs text-slate-500 leading-relaxed">Riparazione o sostituzione per un problema specifico</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-blue-600">
                  Richiedi preventivo <ArrowRight className="w-3 h-3" />
                </div>
              </CardContent>
            </Card>
            <Card
              className="card-hover cursor-pointer border border-emerald-200 shadow-sm rounded-xl overflow-hidden relative"
              onClick={() => navigate('/check-generale')}
              data-testid="service-check-generale"
            >
              <div className="absolute top-3 right-3 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                Consigliato
              </div>
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-emerald-600 rounded-xl flex items-center justify-center mb-3">
                  <ClipboardCheck className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-sm text-slate-900 mb-1">Check Generale</p>
                <p className="text-xs text-slate-500 leading-relaxed">Controllo completo, da 100 EUR. Durata ~1h 30min</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-emerald-600">
                  Prenota il check <ArrowRight className="w-3 h-3" />
                </div>
              </CardContent>
            </Card>
            <Card
              className="card-hover cursor-pointer border-0 shadow-sm rounded-xl overflow-hidden"
              onClick={() => navigate('/abbonamento')}
              data-testid="service-abbonamento"
            >
              <CardContent className="p-5">
                <div className="w-11 h-11 bg-violet-600 rounded-xl flex items-center justify-center mb-3">
                  <Repeat className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-sm text-slate-900 mb-1">Abbonamento</p>
                <p className="text-xs text-slate-500 leading-relaxed">Manutenzione programmata 1-2 volte all'anno</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-violet-600">
                  Richiedi info <ArrowRight className="w-3 h-3" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* My Appointments */}
        <div className="mb-10">
          <Card
            className="card-hover cursor-pointer border-0 shadow-sm bg-white rounded-xl"
            onClick={() => navigate('/appuntamenti')}
            data-testid="view-appointments-btn"
          >
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-slate-700" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm text-slate-900">I Miei Appuntamenti</p>
                <p className="text-xs text-slate-500">{appointments.length} totali</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400" />
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Appointments */}
        {upcoming.length > 0 && (
          <div className="mb-10">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Prossimi Appuntamenti
            </h3>
            <div className="space-y-3">
              {upcoming.slice(0, 3).map((a) => (
                <Card key={a.appointment_id} className="border-0 shadow-sm rounded-xl" data-testid={`appointment-${a.appointment_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex flex-col items-center justify-center">
                        <span className="text-xs text-blue-600 font-bold">{new Date(a.date).toLocaleDateString('it-IT', { month: 'short' })}</span>
                        <span className="text-lg font-bold text-blue-700">{new Date(a.date).getDate()}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{a.time_slot}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Zona {a.zone}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${STATUS_MAP[a.status]?.cls || 'status-pending'} border-0 text-xs px-3 py-1`}>
                      {STATUS_MAP[a.status]?.label || a.status}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Recent Quotes */}
        <div>
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            Preventivi Recenti
          </h3>
          {quotes.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-8 text-center">
                <p className="text-slate-500 text-sm">Nessun preventivo ancora. Creane uno nuovo!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {quotes.slice(0, 5).map((q) => {
                const action = getNextAction(q);
                return (
                  <Card key={q.quote_id} className="border-0 shadow-sm rounded-xl" data-testid={`quote-${q.quote_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Badge className={`${STATUS_MAP[q.status]?.cls || 'status-pending'} border-0 text-xs px-3 py-1`}>
                            {STATUS_MAP[q.status]?.label || q.status}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {new Date(q.created_at).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                        <p className="font-bold text-slate-900">
                          EUR {q.price_min} - {q.price_max}
                        </p>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-600">
                          {q.fixtures?.map((f) => `${f.quantity}x ${f.type}`).join(', ')}
                        </p>
                        {action && (
                          <Button
                            size="sm"
                            variant={action.action === 'accept' ? 'default' : 'outline'}
                            className={action.action === 'accept' ? 'bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs' : 'rounded-full text-xs'}
                            onClick={() => action.action === 'accept' ? handleAccept(q.quote_id) : navigate(action.path)}
                            data-testid={`quote-action-${q.quote_id}`}
                          >
                            {action.label}
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
